import { useEffect, useMemo, useState } from "react";
import { FormCard, FormDialog, FormSearchSelect } from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import {
  meetingsApi,
  type MeetingAgendaItem,
  type MeetingListItem,
} from "../../../api/meetingsApi";
import { toClientApiError } from "../../../api/httpClient";
import { formatMeetingDateTime, MEETING_STATUS_LABELS } from "../constants";

type MeetingOption = MeetingListItem & { label: string };

type Props = {
  open: boolean;
  onClose: () => void;
  item: MeetingAgendaItem | null;
  currentMeetingId: string;
  onMoved?: () => void;
};

function filterLocalOptions(
  options: MeetingOption[],
  search: string,
): MeetingOption[] {
  const q = search.trim().toLowerCase();
  if (!q) return options;
  return options.filter((o) => o.label.toLowerCase().includes(q));
}

export function MoveAgendaItemDialog({
  open,
  onClose,
  item,
  currentMeetingId,
  onMoved,
}: Props) {
  const [meetings, setMeetings] = useState<MeetingListItem[]>([]);
  const [search, setSearch] = useState("");
  const [target, setTarget] = useState<MeetingOption | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setTarget(null);
    setSearch("");
    setError("");
    setLoading(true);
    Promise.all([
      meetingsApi.list({ status: "preparation", pageSize: 50 }),
      meetingsApi.list({ status: "scheduled", pageSize: 50 }),
    ])
      .then(([prep, sched]) => {
        const rows = [...(prep.items || []), ...(sched.items || [])].filter(
          (m) => m.id !== currentMeetingId,
        );
        const byId = new Map<string, MeetingListItem>();
        rows.forEach((m) => {
          if (!byId.has(m.id)) byId.set(m.id, m);
        });
        setMeetings(Array.from(byId.values()));
      })
      .catch((err) => {
        setError(toClientApiError(err).message || "שגיאה בטעינת ישיבות");
      })
      .finally(() => setLoading(false));
  }, [open, currentMeetingId]);

  const options = useMemo(() => {
    const mapped = meetings.map((m) => ({
      ...m,
      label: `${m.title || "ישיבה"} — ${formatMeetingDateTime(m.scheduledAt) || MEETING_STATUS_LABELS[m.status] || m.status}`,
    }));
    return filterLocalOptions(mapped, search);
  }, [meetings, search]);

  const handleSave = async () => {
    if (!item?.id || !target?.id) {
      setError("יש לבחור ישיבת יעד");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await meetingsApi.moveAgendaItem(currentMeetingId, item.id, {
        targetMeetingId: target.id,
      });
      onMoved?.();
      onClose();
    } catch (err) {
      setError(toClientApiError(err).message || "שגיאה בהעברה");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog open={open} onClose={saving ? undefined : onClose} size="sm">
      <FormCard
        title="העברת נושא לישיבה אחרת"
        error={error}
        onSave={() => {
          void handleSave();
        }}
        onCancel={onClose}
        saving={saving}
        saveLabel="העבר"
      >
        <p style={{ marginTop: 0 }}>
          נושא: <strong>{item?.title}</strong>
        </p>
        <FormSearchSelect
          label="ישיבת יעד"
          required
          options={options}
          value={target}
          onChange={(v) => setTarget(v as MeetingOption | null)}
          onInputChange={setSearch}
          getOptionLabel={(o) => (o as MeetingOption)?.label || ""}
          isOptionEqual={(a, b) =>
            (a as MeetingOption | null)?.id === (b as MeetingOption | null)?.id
          }
          loading={loading}
          placeholder="חפש ישיבה..."
          portal
        />
      </FormCard>
    </FormDialog>
  );
}
